import React from 'react';
import { FancyIconButton } from '../../common/buttons';
import AddIcon from '@mui/icons-material/Add';
import { useDispatch, useSelector } from 'react-redux';
import { setDialogOpen } from '../../../store/Actions';

/**
 * AddEventButton — opens the Edit Event dialog with a fresh event.
 *
 * The new event is prepopulated from any active filters in the store
 * (selectedCategoryIds, selectedFriendIds, selectedTripIds, selectedTagIds,
 * selectedStartDate, selectedEndDate), so a user who has filtered the
 * map/timeline by, say, "Maui Trip + 2024" lands in the new-event form with
 * those values already filled in.
 *
 * Props:
 *   variant, size, shape, className, style, ariaLabel — forwarded to
 *     FancyIconButton so the same component can render with different looks
 *     in the header, map overlay, and floating-fab contexts.
 */
export function AddEventButton({
  variant = 'dark',
  size = 'md',
  shape = 'circle',
  className = 'header-add-event',
  style = { width: 38, height: 38 },
  ariaLabel = 'Add Event',
}) {
  const viewingUserAccount = useSelector(state=>state.viewingUserAccount);
  const loggedInUserAccount = useSelector(state=>state.loggedInUserAccount);
  const viewingLoggedInUser = viewingUserAccount && loggedInUserAccount && viewingUserAccount.id == loggedInUserAccount.id;
  const viewingUserCategories = useSelector(state=>state.viewingUserCategories);

  // Active filter values — used to prepopulate the new event so the form
  // matches what the user is currently looking at.
  const selectedCategoryIds = useSelector(state=>state.selectedCategoryIds);
  const selectedFriendIds = useSelector(state=>state.selectedFriendIds);
  const selectedTripIds = useSelector(state=>state.selectedTripIds);
  const selectedTagIds = useSelector(state=>state.selectedTagIds);
  const selectedStartDate = useSelector(state=>state.selectedStartDate);
  const selectedEndDate = useSelector(state=>state.selectedEndDate);

  const dispatch = useDispatch();

  const openNewEventDialog = () => {
    const categoryList = Object.values(viewingUserCategories || {});
    if(categoryList.length === 0){
      alert('Create a category first.');
      return;
    }

    // Prefer the first selected category filter when one is active so the
    // event opens in the same category the user is viewing. Fall back to
    // the first available category otherwise (mirrors the original
    // behavior).
    const filteredCategoryId = (selectedCategoryIds || []).find((id) => Boolean(viewingUserCategories[id]));
    const categoryId = filteredCategoryId || categoryList[0].id;

    const newEvent = {
      userId: loggedInUserAccount.id,
      title: '',
      categoryId,
      friendIds: [...(selectedFriendIds || [])],
      tripIds: [...(selectedTripIds || [])],
      tagIds: [...(selectedTagIds || [])],
    };

    // Only set dates if a date filter is actively applied. EditEvent
    // tolerates either a JS Date or a Firestore Timestamp here.
    if(selectedStartDate){
      newEvent.startDate = selectedStartDate;
    }
    if(selectedEndDate){
      newEvent.endDate = selectedEndDate;
    }

    dispatch(setDialogOpen('Edit Event', {event:newEvent}));
  }

  if(!viewingLoggedInUser)
    return null;

  return (
        <FancyIconButton
            variant={variant}
            size={size}
            shape={shape}
            ariaLabel={ariaLabel}
            onClick={openNewEventDialog}
            className={className}
            style={style}
        >
            <AddIcon />
        </FancyIconButton>
  );
}
